// src/components/tires/Tires.js
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Search, Filter, Plus, Eye, Edit, Trash2, Download, Upload,
  Package, DollarSign, AlertCircle, Check, X, RefreshCw,
  ArrowRightLeft, AlertTriangle
} from 'lucide-react';
import { tiresAPI, tireTypesAPI, tireBrandsAPI, patternsAPI } from '../../services/garageApi';
import { useApiHandler } from '../../hooks/useApiHandler';
import { useError } from '../../contexts/ErrorContext';
import ManageStockModal from '../common/ManageStockModal';

// Image component with fallback to prevent infinite loops
const ImageWithFallback = ({ src, alt, fallback, className = "w-full h-full object-cover" }) => {
  const [hasError, setHasError] = useState(false);
  const [imgSrc, setImgSrc] = useState(src);

  useEffect(() => {
    setImgSrc(src);
    setHasError(false);
  }, [src]);

  const handleError = () => {
    if (!hasError) {
      setHasError(true);
      setImgSrc(null);
    }
  };

  if (hasError || !imgSrc) {
    return <div className={className.replace('object-cover', 'flex items-center justify-center')}>{fallback}</div>;
  }

  return (
    <img
      src={imgSrc}
      alt={alt}
      className={className}
      onError={handleError}
    />
  );
};

const Tires = () => {
  const location = useLocation();
  const { executeApiCall } = useApiHandler();
  const { isLoading } = useError();

  const [tires, setTires] = useState([]);
  const [tireTypes, setTireTypes] = useState([]);
  const [tireBrands, setTireBrands] = useState([]);
  const [allPatterns, setAllPatterns] = useState([]);
  const [error, setError] = useState('');
  const [selectedTires, setSelectedTires] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [tireToDelete, setTireToDelete] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  // Manage Stock Modal
  const [stockModalOpen, setStockModalOpen] = useState(false);
  const [stockModalData, setStockModalData] = useState(null);

  const [filters, setFilters] = useState({
    search: '',
    tire_type_id: '',
    tire_brand_id: '',
    pattern_id: '',
    construction_type: '',
    run_flat: '',
    fuel_rating: '',
    water_rating: '',
    price_min: '',
    price_max: '',
    in_stock: ''
  });

  useEffect(() => {
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      window.history.replaceState({}, document.title);
      setTimeout(() => setSuccessMessage(''), 5000);
    }
  }, [location]);

  const loadDropdownData = useCallback(async () => {
    // Load each dropdown independently so one failure doesn't kill all filters
    const loadTypes = async () => {
      try {
        const response = await executeApiCall(() => tireTypesAPI.getAll(), { showLoading: false });
        setTireTypes(response.data || []);
      } catch (error) {
        console.error('Error loading tire types:', error);
        setTireTypes([]);
      }
    };
    const loadBrands = async () => {
      try {
        const response = await executeApiCall(() => tireBrandsAPI.getAll(), { showLoading: false });
        setTireBrands(response.data || []);
      } catch (error) {
        console.error('Error loading tire brands:', error);
        setTireBrands([]);
      }
    };
    const loadPatterns = async () => {
      try {
        const response = await executeApiCall(() => patternsAPI.getAll(), { showLoading: false });
        setAllPatterns(response.data || []);
      } catch (error) {
        console.error('Error loading patterns:', error);
        setAllPatterns([]);
      }
    };
    await Promise.all([loadTypes(), loadBrands(), loadPatterns()]);
  }, [executeApiCall]);

  const loadTires = useCallback(async (filtersToUse = filters, pageToUse = currentPage) => {
    setError('');
    try {
      const cleanFilters = Object.entries(filtersToUse).reduce((acc, [key, value]) => {
        if (value !== '' && value !== null && value !== undefined) {
          acc[key] = value;
        }
        return acc;
      }, {});

      const params = {
        ...cleanFilters,
        page: pageToUse,
        per_page: 15
      };

      const response = await executeApiCall(
        () => tiresAPI.getAll(params),
        {
          showLoading: false,
          showSuccess: false,
          errorOptions: { showToast: false }
        }
      );

      let tiresList = [];
      let paginationInfo = null;

      if (response && response.data) {
        tiresList = Array.isArray(response.data) ? response.data : [];
        paginationInfo = response.pagination;
      } else if (response && Array.isArray(response)) {
        tiresList = response;
      }

      // Process individual tire data
      tiresList = tiresList.map(tire => {
        tire.quantity = parseInt(tire.quantity) || 0;
        tire.unallocated_quantity = parseInt(tire.unallocated_quantity) || 0;
        tire.has_unallocated = tire.has_unallocated || false;
        return tire;
      });

      setTires(tiresList);

      if (paginationInfo) {
        setTotalPages(paginationInfo.last_page || 1);
        setCurrentPage(paginationInfo.current_page || 1);
      } else {
        setTotalPages(1);
      }

    } catch (error) {
      console.error('Error loading tires:', error);
      setError('Failed to load tires. Please try again.');
      setTires([]);
      setTotalPages(1);
    }
  }, [filters, currentPage, executeApiCall]);

  useEffect(() => {
    loadDropdownData();
  }, [loadDropdownData]);

  useEffect(() => {
    loadTires();
  }, [filters, currentPage, loadTires]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setCurrentPage(1);
  };

  const handleReset = () => {
    setFilters({
      search: '',
      tire_type_id: '',
      tire_brand_id: '',
      pattern_id: '',
      construction_type: '',
      run_flat: '',
      fuel_rating: '',
      water_rating: '',
      price_min: '',
      price_max: '',
      in_stock: ''
    });
    setCurrentPage(1);
  };

  const handleDelete = async (id) => {
    setTireToDelete(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!tireToDelete) return;

    try {
      await tiresAPI.delete(tireToDelete);
      setSuccessMessage('Tire deleted successfully!');
      setShowDeleteModal(false);
      setTireToDelete(null);
      await loadTires();
    } catch (error) {
      console.error('Error deleting tire:', error);
      setError(
        error.response?.data?.message ||
        'Failed to delete tire. It may be referenced in other records.'
      );
      setShowDeleteModal(false);
      setTireToDelete(null);
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedTires(tires.map(t => t.id));
    } else {
      setSelectedTires([]);
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Delete ${selectedTires.length} tires?`)) return;

    let successCount = 0;
    let errorCount = 0;

    for (const id of selectedTires) {
      try {
        await tiresAPI.delete(id);
        successCount++;
      } catch (error) {
        console.error(`Error deleting tire ${id}:`, error);
        errorCount++;
      }
    }

    setSelectedTires([]);
    loadTires();

    if (successCount > 0 && errorCount === 0) {
      setSuccessMessage(`${successCount} tires deleted successfully!`);
    } else if (successCount > 0 && errorCount > 0) {
      setSuccessMessage(`${successCount} tires deleted successfully. ${errorCount} failed.`);
    }
  };

  // Group tires by size + brand + pattern + type
  const groupTires = (tiresList) => {
    const groups = {};
    tiresList.forEach(tire => {
      const key = `${tire.size || ''}-${tire.tire_brand_id || ''}-${tire.pattern_id || ''}-${tire.tire_type_id || ''}`;
      if (!groups[key]) {
        groups[key] = {
          key,
          size: tire.size,
          tire_brand: tire.tire_brand,
          tire_type: tire.tire_type,
          pattern: tire.pattern,
          image: tire.image,
          items: [],
          allIds: [],
          totalQuantity: 0,
          hasUnallocated: false,
        };
      }
      groups[key].items.push(tire);
      groups[key].allIds.push(tire.id);
      groups[key].totalQuantity += parseInt(tire.quantity || 0);
      if (tire.has_unallocated) groups[key].hasUnallocated = true;
      // Use first available image
      if (!groups[key].image && tire.image) groups[key].image = tire.image;
    });
    return Object.values(groups);
  };

  const hasActiveFilter = Object.entries(filters).some(([key, value]) => value !== '');
  const groupedTires = hasActiveFilter ? groupTires(tires) : tires.map(tire => ({
    key: tire.id,
    size: tire.size,
    tire_brand: tire.tire_brand,
    tire_type: tire.tire_type,
    pattern: tire.pattern,
    image: tire.image,
    items: [tire],
    allIds: [tire.id],
    totalQuantity: parseInt(tire.quantity || 0),
    hasUnallocated: tire.has_unallocated || false,
  }));

  const getGroupStockStatus = (group) => {
    if (group.hasUnallocated) return { text: 'Pending Allocation', color: 'yellow', icon: AlertTriangle };
    const outOfStockCount = group.items.filter(t => parseInt(t.quantity || 0) === 0).length;
    if (group.totalQuantity === 0) return { text: 'Out of Stock', color: 'red', icon: X };
    if (outOfStockCount > 0) return { text: `${outOfStockCount} Out of Stock`, color: 'yellow', icon: AlertCircle };
    if (group.totalQuantity < 10) return { text: 'Low Stock', color: 'yellow', icon: AlertCircle };
    return { text: 'In Stock', color: 'green', icon: Check };
  };

  const openStockModal = (group) => {
    setStockModalData({
      productIds: group.allIds,
      productLabel: `${group.size} ${group.tire_brand?.name || ''} ${group.pattern?.name || ''}`.trim(),
    });
    setStockModalOpen(true);
  };

  const stats = {
    totalProducts: tires.length,
    totalValue: tires.reduce((sum, tire) => {
      return sum + (parseInt(tire.quantity || 0) * parseFloat(tire.selling_price || 0));
    }, 0),
    lowStock: tires.filter(tire => {
      const qty = parseInt(tire.quantity || 0);
      return qty > 0 && qty < 10;
    }).length,
    outOfStock: tires.filter(tire => parseInt(tire.quantity || 0) === 0).length
  };

  // Tire Card Component (Grid View) — renders a grouped set of variants
  const TireCard = ({ group }) => {
    const stock = getGroupStockStatus(group);
    const StatusIcon = stock.icon;
    const isMulti = group.items.length > 1;

    return (
      <div className={`bg-white border rounded-xl hover:shadow-lg transition-all overflow-hidden ${group.hasUnallocated ? 'border-yellow-300 shadow-yellow-100/50' : 'border-gray-200'}`}>
        <div className="aspect-square relative bg-gray-100">
          {group.image ? (
            <ImageWithFallback
              src={group.image}
              alt={group.size}
              className="w-full h-full object-cover"
              fallback={<Package className="w-16 h-16 text-gray-400" />}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-150">
              <Package className="w-16 h-16 text-gray-300" />
            </div>
          )}

          {/* Stock badge */}
          <div className={`absolute bottom-2 right-2 px-2.5 py-1 bg-${stock.color}-100/90 text-${stock.color}-700 text-xs font-semibold rounded-lg flex items-center gap-1 backdrop-blur-sm`}>
            <StatusIcon className="w-3 h-3" />
            {group.totalQuantity} units
          </div>
          {isMulti && (
            <div className="absolute top-2 left-2 px-2 py-0.5 bg-blue-600/90 text-white text-xs font-semibold rounded-full backdrop-blur-sm shadow-sm">
              {group.items.length} variants
            </div>
          )}
        </div>

        <div className="p-4">
          <div className="flex items-start justify-between mb-2">
            <div>
              <h3 className="font-bold text-lg leading-tight">{group.size || 'No Size'}</h3>
              <p className="text-sm text-gray-600">
                {group.tire_brand?.name || 'Unknown Brand'}
              </p>
            </div>
            <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded">
              {group.tire_type?.name || 'N/A'}
            </span>
          </div>

          {group.pattern?.name && (
            <p className="text-xs text-gray-500 mb-3">{group.pattern.name}</p>
          )}

          {/* Unallocated warning */}
          {group.hasUnallocated && (
            <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-yellow-50 border border-yellow-200 text-yellow-700 text-xs font-medium rounded-lg mb-3">
              <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
              Pending Allocation
            </div>
          )}

          {/* Variant details table */}
          <div className={`rounded-lg border border-gray-100 overflow-hidden ${isMulti ? 'mb-1' : ''}`}>
            <div className="grid grid-cols-3 text-[10px] font-medium text-gray-400 uppercase tracking-wider px-2.5 py-1.5 bg-gray-50 border-b border-gray-100">
              <span>DOT</span>
              <span className="text-right">Price</span>
              <span className="text-right">Qty</span>
            </div>
            {group.items.map((tire, i) => {
              const qty = parseInt(tire.quantity || 0);
              return (
                <div key={tire.id} className={`grid grid-cols-3 items-center px-2.5 py-2 text-sm ${i > 0 ? 'border-t border-gray-50' : ''} ${i % 2 === 1 ? 'bg-gray-50/50' : ''}`}>
                  <span className="text-gray-600 text-xs truncate pr-1">
                    {tire.dot_formatted || tire.dot || 'N/A'}
                  </span>
                  <span className="text-right font-semibold text-gray-900">${tire.selling_price || '0'}</span>
                  <span className={`text-right font-medium ${
                    qty === 0 ? 'text-red-500' : qty < 10 ? 'text-yellow-600' : 'text-green-600'
                  }`}>
                    {qty}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="px-4 py-3 bg-gray-50/80 border-t border-gray-100">
          {/* Per-variant actions */}
          <div className="space-y-1">
            {group.items.map((tire) => (
              <div key={tire.id} className={`flex items-center justify-between ${isMulti ? 'px-1.5 py-1 rounded-md hover:bg-white' : ''}`}>
                <span className="text-xs text-gray-400 truncate max-w-[100px] font-mono">
                  {tire.dot || tire.id.slice(-6)}
                </span>
                <div className="flex items-center gap-0.5">
                  <Link
                    to={`/tires/${tire.id}`}
                    className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                    title="View"
                  >
                    <Eye className="w-3.5 h-3.5" />
                  </Link>
                  <Link
                    to={`/tires/${tire.id}/edit`}
                    className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                    title="Edit"
                  >
                    <Edit className="w-3.5 h-3.5" />
                  </Link>
                  <button
                    onClick={() => handleDelete(tire.id)}
                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
          {/* Group-level manage stock */}
          <button
            onClick={() => openStockModal(group)}
            className={`mt-2 w-full flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
              group.hasUnallocated
                ? 'bg-yellow-50 border border-yellow-300 text-yellow-800 hover:bg-yellow-100'
                : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300'
            }`}
          >
            {group.hasUnallocated && <AlertTriangle className="w-3.5 h-3.5" />}
            <ArrowRightLeft className="w-3.5 h-3.5" />
            Manage Stock
          </button>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="text-gray-600">Loading tires...</p>
        </div>
      </div>
    );
  }

  if (error && tires.length === 0) {
    return (
      <div className="m-4 rounded-lg border border-red-400 bg-red-50 px-4 py-3 text-red-700">
        <div className="flex items-center gap-2 mb-3">
          <AlertCircle className="w-5 h-5" />
          <span className="font-medium">Error loading tires</span>
        </div>
        <p className="mb-3">{error}</p>
        <button
          onClick={loadTires}
          className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors flex items-center gap-2"
        >
          <RefreshCw className="w-4 h-4" />
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Success Message */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 text-green-800 flex items-center gap-2">
          <Check className="w-5 h-5" />
          <span>{successMessage}</span>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Products</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{stats.totalProducts}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <Package className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Value</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">${stats.totalValue.toFixed(2)}</p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <DollarSign className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Low Stock</p>
              <p className="text-2xl font-bold text-yellow-600 mt-1">{stats.lowStock}</p>
            </div>
            <div className="p-3 bg-yellow-100 rounded-lg">
              <AlertCircle className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Out of Stock</p>
              <p className="text-2xl font-bold text-red-600 mt-1">{stats.outOfStock}</p>
            </div>
            <div className="p-3 bg-red-100 rounded-lg">
              <X className="w-6 h-6 text-red-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        {/* Header */}
        <div className="p-6 border-b border-gray-100">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Tire Inventory</h2>
              <p className="text-sm text-gray-600 mt-1">Manage your tire products and inventory</p>
            </div>
            <div className="flex items-center gap-3">
              <button className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2">
                <Upload className="w-4 h-4" />
                Import
              </button>
              <button className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2">
                <Download className="w-4 h-4" />
                Export
              </button>
              <Link
                to="/tires/create"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Add New Tire
              </Link>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="p-6 border-b border-gray-100 bg-gray-50/50">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  name="search"
                  placeholder="Search by size, brand, or pattern..."
                  value={filters.search}
                  onChange={handleFilterChange}
                  className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`px-4 py-2.5 rounded-lg border transition-all flex items-center gap-2 ${
                  showFilters
                    ? 'bg-blue-50 border-blue-200 text-blue-700'
                    : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Filter className="w-4 h-4" />
                Filters
              </button>
              <div className="flex items-center bg-white border border-gray-200 rounded-lg">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2.5 ${viewMode === 'grid' ? 'text-blue-600' : 'text-gray-400'}`}
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                  </svg>
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2.5 ${viewMode === 'list' ? 'text-blue-600' : 'text-gray-400'}`}
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          {/* Extended Filters */}
          {showFilters && (
            <div className="mt-4 p-4 bg-white border border-gray-200 rounded-lg">
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
                <select
                  name="tire_brand_id"
                  value={filters.tire_brand_id}
                  onChange={handleFilterChange}
                  className="px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Brands</option>
                  {tireBrands.map(brand => (
                    <option key={brand.id} value={brand.id}>{brand.name}</option>
                  ))}
                </select>

                <select
                  name="pattern_id"
                  value={filters.pattern_id}
                  onChange={handleFilterChange}
                  className="px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Patterns</option>
                  {allPatterns.map(pattern => (
                    <option key={pattern.id} value={pattern.id}>{pattern.name}</option>
                  ))}
                </select>

                <select
                  name="tire_type_id"
                  value={filters.tire_type_id}
                  onChange={handleFilterChange}
                  className="px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Types</option>
                  {tireTypes.map(type => (
                    <option key={type.id} value={type.id}>{type.name}</option>
                  ))}
                </select>

                <select
                  name="in_stock"
                  value={filters.in_stock}
                  onChange={handleFilterChange}
                  className="px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Stock Status</option>
                  <option value="1">In Stock</option>
                  <option value="low">Low Stock</option>
                  <option value="0">Out of Stock</option>
                </select>

                <button
                  onClick={handleReset}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Reset Filters
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Selected Items Bar */}
        {selectedTires.length > 0 && (
          <div className="px-6 py-3 bg-blue-50 border-b border-blue-100 flex items-center justify-between">
            <span className="text-sm text-blue-700">
              {selectedTires.length} selected
            </span>
            <button
              onClick={handleBulkDelete}
              className="px-3 py-1.5 text-sm text-red-600 bg-white border border-red-200 rounded-lg hover:bg-red-50"
            >
              Delete Selected
            </button>
          </div>
        )}

        {/* Content */}
        {viewMode === 'grid' ? (
          <div className="p-6">
            {groupedTires.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {groupedTires.map((group) => (
                  <TireCard key={group.key} group={group} />
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No tires found</h3>
                <p className="text-gray-600 mb-4">Add your first tire or adjust filters</p>
                <Link
                  to="/tires/create"
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add First Tire
                </Link>
              </div>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left w-10">
                    <input
                      type="checkbox"
                      onChange={handleSelectAll}
                      checked={tires.length > 0 && selectedTires.length >= tires.length}
                      className="rounded border-gray-300"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Image</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Size</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Brand</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pattern</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">DOT</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Price</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stock</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {groupedTires.length > 0 ? (
                  groupedTires.map((group) => {
                    const stock = getGroupStockStatus(group);
                    const StatusIcon = stock.icon;
                    const isGroupSelected = group.allIds.every(id => selectedTires.includes(id));
                    const isSomeSelected = group.allIds.some(id => selectedTires.includes(id));
                    const isMulti = group.items.length > 1;

                    return (
                      <tr key={group.key} className={`hover:bg-gray-50/50 transition-colors ${group.hasUnallocated ? 'bg-yellow-50/20' : ''}`}>
                        {/* Checkbox */}
                        <td className="px-4 py-4 align-top">
                          <input
                            type="checkbox"
                            checked={isGroupSelected}
                            ref={el => { if (el) el.indeterminate = isSomeSelected && !isGroupSelected; }}
                            onChange={() => {
                              if (isGroupSelected) {
                                setSelectedTires(prev => prev.filter(id => !group.allIds.includes(id)));
                              } else {
                                setSelectedTires(prev => [...new Set([...prev, ...group.allIds])]);
                              }
                            }}
                            className="rounded border-gray-300"
                          />
                        </td>
                        {/* Image */}
                        <td className="px-4 py-4 align-top">
                          <div className="w-12 h-12 bg-gray-100 rounded-lg overflow-hidden relative">
                            {group.image ? (
                              <ImageWithFallback
                                src={group.image}
                                alt={group.size}
                                fallback={<Package className="w-6 h-6 text-gray-400" />}
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Package className="w-6 h-6 text-gray-300" />
                              </div>
                            )}
                            {isMulti && (
                              <span className="absolute -top-1 -right-1 w-5 h-5 bg-blue-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center ring-2 ring-white">
                                {group.items.length}
                              </span>
                            )}
                          </div>
                        </td>
                        {/* Size */}
                        <td className="px-4 py-4 align-top">
                          <div className="font-semibold text-gray-900">{group.size || 'N/A'}</div>
                          {isMulti && (
                            <span className="text-[10px] text-gray-400 mt-0.5 block">{group.items.length} variants</span>
                          )}
                        </td>
                        {/* Brand */}
                        <td className="px-4 py-4 align-top text-gray-700">
                          {group.tire_brand?.name || 'Unknown'}
                        </td>
                        {/* Pattern */}
                        <td className="px-4 py-4 align-top text-sm text-gray-500">
                          {group.pattern?.name || '-'}
                        </td>
                        {/* Type */}
                        <td className="px-4 py-4 align-top text-gray-700">{group.tire_type?.name || 'N/A'}</td>
                        {/* Stacked: DOT */}
                        <td className="px-4 py-4 align-top">
                          <div className={isMulti ? 'divide-y divide-gray-100' : ''}>
                            {group.items.map((tire, i) => (
                              <div key={tire.id} className={`text-sm text-gray-600 ${isMulti && i > 0 ? 'pt-2 mt-2' : ''}`}>
                                {tire.dot_formatted || tire.dot || 'N/A'}
                              </div>
                            ))}
                          </div>
                        </td>
                        {/* Stacked: Price */}
                        <td className="px-4 py-4 align-top">
                          <div className={isMulti ? 'divide-y divide-gray-100' : ''}>
                            {group.items.map((tire, i) => (
                              <div key={tire.id} className={isMulti && i > 0 ? 'pt-2 mt-2' : ''}>
                                <div className="font-semibold text-gray-900">${tire.selling_price || '0'}</div>
                                <div className="text-[11px] text-gray-400">Cost: ${tire.cost_price || '0'}</div>
                              </div>
                            ))}
                          </div>
                        </td>
                        {/* Stacked: Stock + aggregate status */}
                        <td className="px-4 py-4 align-top">
                          <div className={isMulti ? 'divide-y divide-gray-100' : ''}>
                            {group.items.map((tire, i) => {
                              const qty = parseInt(tire.quantity || 0);
                              return (
                                <div key={tire.id} className={`flex items-center gap-1.5 ${isMulti && i > 0 ? 'pt-2 mt-2' : ''}`}>
                                  <span className={`text-sm font-semibold tabular-nums ${
                                    qty === 0 ? 'text-red-500' : qty < 10 ? 'text-yellow-600' : 'text-green-600'
                                  }`}>
                                    {qty}
                                  </span>
                                  {qty === 0 && (
                                    <span className="w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0" />
                                  )}
                                </div>
                              );
                            })}
                          </div>
                          {/* Aggregate status badge */}
                          <div className={`mt-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-${stock.color}-100 text-${stock.color}-700`}>
                            <StatusIcon className="w-3 h-3" />
                            {stock.text}
                          </div>
                          {isMulti && (
                            <div className="text-[11px] text-gray-400 mt-1">
                              Total: <span className="font-semibold text-gray-600">{group.totalQuantity}</span>
                            </div>
                          )}
                        </td>
                        {/* Actions */}
                        <td className="px-4 py-4 align-top">
                          {isMulti ? (
                            <>
                              <div className="divide-y divide-gray-100">
                                {group.items.map((tire, i) => (
                                  <div key={tire.id} className={`flex items-center gap-0.5 ${i > 0 ? 'pt-2 mt-2' : ''}`}>
                                    <Link to={`/tires/${tire.id}`} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors" title="View">
                                      <Eye className="w-3.5 h-3.5" />
                                    </Link>
                                    <Link to={`/tires/${tire.id}/edit`} className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors" title="Edit">
                                      <Edit className="w-3.5 h-3.5" />
                                    </Link>
                                    <button onClick={() => handleDelete(tire.id)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors" title="Delete">
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                              <button
                                onClick={() => openStockModal(group)}
                                className={`mt-2.5 flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                                  group.hasUnallocated
                                    ? 'bg-yellow-50 border border-yellow-300 text-yellow-800 hover:bg-yellow-100'
                                    : 'bg-gray-50 border border-gray-200 text-gray-600 hover:bg-gray-100 hover:border-gray-300'
                                }`}
                                title="Manage Stock"
                              >
                                {group.hasUnallocated && <AlertTriangle className="w-3 h-3" />}
                                <ArrowRightLeft className="w-3 h-3" />
                                Manage Stock
                              </button>
                            </>
                          ) : (
                            <div className="flex items-center gap-1">
                              <Link to={`/tires/${group.items[0].id}`} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors" title="View">
                                <Eye className="w-3.5 h-3.5" />
                              </Link>
                              <Link to={`/tires/${group.items[0].id}/edit`} className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors" title="Edit">
                                <Edit className="w-3.5 h-3.5" />
                              </Link>
                              <button
                                onClick={() => openStockModal(group)}
                                className={`p-1.5 rounded-md transition-colors flex items-center gap-0.5 ${
                                  group.hasUnallocated
                                    ? 'text-yellow-700 hover:bg-yellow-50'
                                    : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100'
                                }`}
                                title="Manage Stock"
                              >
                                {group.hasUnallocated && <AlertTriangle className="w-3 h-3" />}
                                <ArrowRightLeft className="w-3.5 h-3.5" />
                              </button>
                              <button onClick={() => handleDelete(group.items[0].id)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors" title="Delete">
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="10" className="px-6 py-12 text-center text-gray-500">
                      No tires found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 bg-gray-50 border-t flex justify-between items-center">
            <div className="text-sm text-gray-700">
              Page {currentPage} of {totalPages}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 text-sm bg-white border rounded hover:bg-gray-50 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 text-sm bg-white border rounded hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Delete Tire</h3>
            <p className="text-gray-600 mb-6">Are you sure you want to delete this tire?</p>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setTireToDelete(null);
                }}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manage Stock Modal */}
      <ManageStockModal
        isOpen={stockModalOpen}
        onClose={() => {
          setStockModalOpen(false);
          setStockModalData(null);
        }}
        productIds={stockModalData?.productIds || []}
        productLabel={stockModalData?.productLabel || ''}
        productType="tire"
        onStockMoved={() => loadTires()}
      />
    </div>
  );
};

export default Tires;
